// SafeDeskTextEditor.tsx
import { ComponentProps, useCallback } from 'react';
import type { ClipboardEvent as ReactClipboardEvent } from 'react';
import { useTheme } from '@mui/material/styles';
import Editor, {
    BtnBold,
    BtnItalic,
    BtnUnderline,
    BtnStrikeThrough,
    BtnNumberedList,
    BtnBulletList,
    BtnUndo,
    BtnRedo,
    BtnClearFormatting,
    ContentEditableEvent,
    Toolbar,
    Separator,
    HtmlButton,
    BtnStyles,
    createButton
} from 'react-simple-wysiwyg';

const BtnLink = createButton(
    "Link",
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        width={20}
        height={20}
        style={{
            verticalAlign: "text-top"
        }}
    // className={btnSize}
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
        />
    </svg>,
    ({ $selection }) => {
        if ($selection?.nodeName === "A") {
            document.execCommand("unlink");
        } else {
            const Selection = document.getSelection()?.toString();
            // eslint-disable-next-line no-alert -- ignore
            const Uri = prompt("URL", "");
            document.execCommand(
                "insertHTML",
                false,
                Uri ? `<a href="${Uri}" target="_blank">${Selection ? Selection : Uri}</a>` : Selection,
            );
        }
    },
);

interface SafeDeskTextEditorProps {
    value: string;
    onChange: (event: ContentEditableEvent) => void;
    style?: React.CSSProperties;
    disabled?: boolean;
    placeholder?: string;
    containerProps?: ComponentProps<'div'>;
}

export default function SafeDeskTextEditor({ value, onChange, style, disabled, placeholder, containerProps }: SafeDeskTextEditorProps) {
    const theme = useTheme();
    const isDark2 = theme.palette.background.default === '#202326';
    const editorContainerProps = {
        ...containerProps,
        className: [containerProps?.className, isDark2 ? 'rsw-editor-dark2' : undefined].filter(Boolean).join(' ')
    };

    function handleChange(event: ContentEditableEvent) {
        if (event.target.value === '<br>') {
            event.target.value = '';
        }
        onChange(event);
    }

    const handlePaste = useCallback((event: ReactClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain');
        if (!text) {
            return;
        }

        const sanitized = text.replace(/\r\n/g, '\n');

        const insertCommandSupported = typeof document.queryCommandSupported === 'function' && document.queryCommandSupported('insertText');

        if (insertCommandSupported) {
            document.execCommand('insertText', false, sanitized);
            return;
        }

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }

        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(sanitized));
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    }, []);

    return (
        <Editor
            style={{ ...style, whiteSpace: 'pre-wrap' }}
            value={value}
            onChange={handleChange}
            onPaste={handlePaste}
            disabled={disabled}
            placeholder={placeholder}
            containerProps={editorContainerProps}
        >
            <Toolbar {...(isDark2 ? { className: 'rsw-toolbar rsw-toolbar-dark2' } : {})}>
                <BtnBold />
                <BtnItalic />
                <BtnUnderline />
                <BtnStrikeThrough />
                <Separator />
                <BtnNumberedList />
                <BtnBulletList />
                <Separator />
                <BtnLink />
                <BtnClearFormatting />
                <HtmlButton />
                <Separator />
                <BtnStyles />
            </Toolbar>
        </Editor>
    );
}
